/**
 * サービスの組み立て(ローカルモード ⇄ Firebase モードの唯一の切替点)。
 *
 * `VITE_FIREBASE_*` が揃っていれば Firebase モード、
 * 1つでも欠けていれば**現行どおり**ローカルモード(localStorage + クライアント抽選)で動く。
 * 画面・ストアはこの違いを知らない(`ProgressService` / `GachaService` しか見ない)。
 *
 * Firebase SDK は**動的 import**(`./firebase/runtime`)。ローカルモードのユーザーは
 * SDK チャンクを読み込まない(CLAUDE.md ルール6: Chromebook の初回3秒台)。
 * 取得を待つあいだの隙間は `LazyProgressService` / `LazyGachaService` が吸収する。
 *
 * 必要な env は `apps/web/.env.example` を参照。
 *
 *   # ローカルモード(既定)
 *   pnpm --filter @paltyping/web dev
 *
 *   # エミュレータに繋いで動かす
 *   PALTYPING_ENFORCE_APP_CHECK=false pnpm --filter @paltyping/functions emulators
 *   cp apps/web/.env.example apps/web/.env.local   # Firebase 節の「エミュレータ」ぶんを有効化
 *   pnpm --filter @paltyping/web dev
 */
import { UnavailableAuthService, type AuthService } from './auth';
import { NoChallengeService, type ChallengeService } from './challenge';
import { ContentService } from './content';
import { LocalGachaService, type GachaService } from './gacha';
import { LocalInventoryService, type InventoryService } from './inventory';
import { NoLeagueService, type LeagueService } from './league';
import { NoTutorService, type TutorService } from './tutor';
import { NoTransferService, type TransferService } from './transfer';
import { LocalProgressService, type ProgressService } from './progress';
import {
  LocalQuizProgressService,
  type HydratableQuizProgress,
  type QuizProgressService,
} from './quizProgress';
import { NoPuzzleRewardService, type PuzzleRewardService } from './puzzleRewards';
import { NoQuizRewardService, type QuizRewardService } from './quizRewards';
import {
  LazyAuthService,
  LazyChallengeService,
  LazyGachaService,
  LazyLeagueService,
  LazyProgressService,
  LazyPuzzleRewardService,
  LazyQuizRewardService,
  LazyTransferService,
  LazyTutorService,
} from './lazy';
// SDK に触れない env 判定だけを静的に読む(バレル経由にすると SDK ごと束ねられる)。
import {
  defaultEnvSource,
  hasAnyFirebaseEnv,
  missingFirebaseEnvKeys,
  readFirebaseEnv,
  type EnvSource,
  type FirebaseEnv,
} from './firebase/env';
import type { FirebaseRuntime, FirebaseRuntimeDeps } from './firebase/runtime';
import { backupAndClearRecoveryCache, requiresRecovery } from './firebase/recovery';

export type ServiceMode = 'local' | 'firebase';

export interface AppServices {
  readonly mode: ServiceMode;
  readonly content: ContentService;
  readonly progress: ProgressService;
  readonly inventory: InventoryService;
  /**
   * まなびモードの進捗(docs/10 §2)。
   *
   * **アプリで1つだけ**にしてある —— サーバーからの読み戻し(docs/04 §5.1)は
   * このインスタンスのキャッシュを書き換えるので、まなびの画面が別のインスタンスを
   * 持っていると古い星を持ち続けてしまう。
   */
  readonly quizProgress: QuizProgressService & HydratableQuizProgress;
  readonly gacha: GachaService;
  /**
   * 登録・ログイン(docs/10_paltown_platform.md §3.1)。
   * ローカルモードでは `supported: false` = 画面に導線を出さない。
   */
  readonly auth: AuthService;
  /**
   * パズルのコイン報酬(docs/11_thinking_module.md §3)。
   * ローカルモードでは何もしない実装 = 画面にコインを出さない。
   */
  readonly puzzleRewards: PuzzleRewardService;
  /**
   * まなびモードの教科コイン報酬(docs/10_paltown_platform.md §4.3)。
   * ローカルモードでは何もしない実装 = 画面にコインを出さない。
   */
  readonly quizRewards: QuizRewardService;
  /**
   * 週替わりチャレンジ(docs/12_challenge_ranking.md §1)。
   * ローカルモードでは `supported: false` = 選択画面にカードを出さない。
   */
  readonly challenge: ChallengeService;
  /**
   * がんばりポイントとリーグ(docs/12_challenge_ranking.md §2)。
   * ローカルモードでは `supported: false` = 選択画面にカードを出さない。
   */
  readonly league: LeagueService;
  /**
   * AIチューター「パルの ひとこと」(docs/07_learning_support.md §7)。
   * ローカルモードでは `supported: false` = 「ログインすると おはなしできるよ」。
   */
  readonly tutor: TutorService;
  /**
   * ひきつぎコード(docs/10_paltown_platform.md §3.4)。
   * ローカルモードでは `supported: false` = 「とうろく」画面にカードを出さない。
   */
  readonly transfer: TransferService;
  /** 実装の解決を待つ(テスト用。ローカルモードでは即 resolve)。 */
  readonly ready: Promise<void>;
  /** 復旧退避に失敗したため、誤送信を避けて開始を停止した。 */
  readonly recoveryBlocked?: boolean;
}

export interface CreateServicesOptions {
  /** 明示的に env を渡す(テスト用)。省略時は `import.meta.env` / `process.env`。 */
  readonly env?: EnvSource;
  /** 解決済みの設定を直接渡す(テスト用)。`null` を渡すと強制的にローカルモード。 */
  readonly firebase?: FirebaseEnv | null;
  readonly onWarn?: (message: string, detail?: unknown) => void;
}

/** ローカルモード一式(Firebase 未設定時の既定)。 */
export function createLocalServices(): AppServices {
  const content = new ContentService();
  const progress = new LocalProgressService();
  const inventory = new LocalInventoryService();
  return {
    mode: 'local',
    content,
    progress,
    inventory,
    quizProgress: new LocalQuizProgressService(),
    gacha: new LocalGachaService({
      loadCatalog: () => content.loadCatalog(),
      inventory,
      wallet: progress,
    }),
    auth: new UnavailableAuthService(),
    puzzleRewards: new NoPuzzleRewardService(),
    quizRewards: new NoQuizRewardService(),
    challenge: new NoChallengeService(),
    league: new NoLeagueService(),
    tutor: new NoTutorService(),
    transfer: new NoTransferService(),
    ready: Promise.resolve(),
  };
}

function createRecoveryBlockedServices(): AppServices {
  return { ...createLocalServices(), recoveryBlocked: true };
}

/**
 * Firebase モード一式。
 *
 * SDK の取得・初期化は非同期。落ちてもゲームは遊べるべきなので、
 * 失敗時はローカル相当の実装へ静かに退避する(白画面にしない)。
 */
export function createFirebaseServices(
  env: FirebaseEnv,
  warn: (message: string, detail?: unknown) => void,
): AppServices {
  const content = new ContentService();
  const inventory = new LocalInventoryService();
  const local = new LocalProgressService();
  const quizProgress = new LocalQuizProgressService();
  const deps: FirebaseRuntimeDeps = { content, inventory, local, quizProgress };

  const runtime: Promise<FirebaseRuntime> = import('./firebase/runtime').then(
    (module) => {
      try {
        return module.createFirebaseRuntime(env, deps);
      } catch (error) {
        warn('Firebase の初期化に失敗したのでローカルモードで動きます', error);
        return module.createLocalFallbackRuntime(deps);
      }
    },
    (error: unknown) => {
      warn('Firebase SDK を読み込めなかったのでローカルモードで動きます', error);
      return {
        progress: local,
        gacha: new LocalGachaService({
          loadCatalog: () => content.loadCatalog(),
          inventory,
          wallet: local,
        }),
        auth: new UnavailableAuthService(),
        puzzleRewards: new NoPuzzleRewardService(),
        quizRewards: new NoQuizRewardService(),
        challenge: new NoChallengeService(),
        league: new NoLeagueService(),
        tutor: new NoTutorService(),
        // 合流はサーバーの専権なので、callable が無ければ ひきつぎ も使えない。
        transfer: new NoTransferService(),
        // callable が無いので読み戻しもできない(表示はキャッシュのまま)。
        hydration: null,
        dispose: () => {},
      } satisfies FirebaseRuntime;
    },
  );

  return {
    mode: 'firebase',
    content,
    inventory,
    quizProgress,
    progress: new LazyProgressService(runtime.then((r) => r.progress)),
    gacha: new LazyGachaService(runtime.then((r) => r.gacha)),
    auth: new LazyAuthService(runtime.then((r) => r.auth)),
    puzzleRewards: new LazyPuzzleRewardService(runtime.then((r) => r.puzzleRewards)),
    quizRewards: new LazyQuizRewardService(runtime.then((r) => r.quizRewards)),
    challenge: new LazyChallengeService(runtime.then((r) => r.challenge)),
    league: new LazyLeagueService(runtime.then((r) => r.league)),
    tutor: new LazyTutorService(runtime.then((r) => r.tutor)),
    transfer: new LazyTransferService(runtime.then((r) => r.transfer)),
    ready: runtime.then(() => undefined),
  };
}

/** env を見てモードを決める。 */
export function createServices(options: CreateServicesOptions = {}): AppServices {
  const warn =
    options.onWarn ?? ((message, detail) => console.warn(`[paltyping] ${message}`, detail));
  const source = options.env ?? defaultEnvSource();
  const env = options.firebase !== undefined ? options.firebase : readFirebaseEnv(source);

  if (!env) {
    // 「半分だけ設定した」状態は事故なので気付けるようにする。
    if (options.firebase === undefined && hasAnyFirebaseEnv(source)) {
      warn(
        `VITE_FIREBASE_* が足りないのでローカルモードで起動します: ${missingFirebaseEnvKeys(
          source,
        ).join(', ')}`,
      );
    }
    return createLocalServices();
  }

  if (requiresRecovery(env.projectId, env.emulator)) {
    try {
      if (
        backupAndClearRecoveryCache(typeof localStorage === 'undefined' ? null : localStorage) ===
        'blocked'
      ) {
        warn('復旧用の端末データを退避できないため、開始を停止しました');
        return createRecoveryBlockedServices();
      }
    } catch {
      warn('復旧用の端末データを退避できないため、開始を停止しました');
      return createRecoveryBlockedServices();
    }
  }

  return createFirebaseServices(env, warn);
}
